import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import SectionHeading from "@/components/SectionHeading";
import { buildFaqJsonLd } from "@/components/GeoDefinition";
import SalesPartnerForm from "@/components/SalesPartnerForm";

const BENEFITS = [
  "Klare Produktwelt",
  "Attraktiver B2B-Markt",
  "Keine technische Umsetzung durch Partner",
  "Keine Installation durch Partner",
  "Keine Supportpflicht durch Partner",
  "Mehrere Zielgruppen: Hotels, Freizeit, Event, Immobilien, Tourismus",
];

const STEPS = [
  { n: "01", t: "Als Sales Partner anfragen", text: "Sie reichen Profil, Region und Netzwerk ein." },
  { n: "02", t: "Netzwerk und Region einordnen", text: "PINE bewertet Ihr Profil und Ihre Zugangsfelder." },
  { n: "03", t: "Lead einreichen", text: "Sie reichen einen qualifizierten Standortkontakt ein." },
  { n: "04", t: "PINE prüft Kontakt und Produktfit", text: "PINE bewertet, ob der Lead zu einem PINE Systems Projekt passen kann." },
  { n: "05", t: "PINE übernimmt Gespräch und Projektprozess", text: "Sie müssen nicht installieren, betreiben oder supporten." },
  { n: "06", t: "Provision bei Vertragsabschluss", text: "Bei erfolgreichem Vertragsabschluss erhalten Sie 899 € netto Provision." },
];

const SALES_FAQ = [
  {
    q: "Kann ich als Sales Partner Standorte empfehlen?",
    a: "Ja. Das PINE Sales Partner Programm richtet sich an Personen mit Zugang zu Hotels, Freizeitflächen, Eventlocations, Immobilien, Tourismus, Investoren oder Betreiberstrukturen.",
  },
  {
    q: "Wann entsteht die Sales Partner Provision?",
    a: "Die Provision entsteht nur, wenn ein vorab eingereichter, PINE bisher nicht bekannter Lead zu einem unterzeichneten Vertrag führt. Die konkrete Abwicklung wird vorab individuell bestätigt.",
  },
  {
    q: "Muss ich Technik installieren oder Support leisten?",
    a: "Nein. PINE übernimmt Beratung, Gespräch, Prüfung und den gesamten Projektprozess. Als Partner stellen Sie den qualifizierten Kontakt her.",
  },
];

export default function SalesPartner() {
  return (
    <>
      <SEO
        title="PINE Sales Partner Programm – Standortkontakte empfehlen"
        description="Werden Sie PINE Sales Partner und vermitteln Sie qualifizierte Kontakte zu Hotels, Freizeitflächen, Eventlocations, Immobilien und Standortbetreibern."
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "PINE Sales Partner Programm",
            url: "https://www.pinexs.de/sales-partner",
            inLanguage: "de-DE",
          },
          buildFaqJsonLd(SALES_FAQ),
        ]}
      />

      {/* HERO */}
      <section className="relative border-b border-[rgba(255,255,255,0.08)] overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-36 md:pb-24">
          <div className="overline" data-testid="sales-eyebrow">PINE Sales Partner Programm</div>
          <h1
            className="font-display text-4xl sm:text-5xl md:text-6xl uppercase mt-4 max-w-5xl leading-[1.04]"
            data-testid="sales-headline"
          >
            Empfehlen Sie Standorte. <span className="text-[#18E096]">Wir bauen daraus Chancen.</span>
          </h1>
          <p className="text-[#AEB7B7] mt-6 max-w-3xl text-base md:text-lg leading-relaxed">
            Für Menschen mit Zugang zu Hotels, Freizeitflächen, Eventlocations, Immobilien,
            Investoren, Tourismus oder Betreiberstrukturen.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#sales-form" className="btn-primary" data-testid="sales-cta-hero">
              Als Sales Partner anfragen <ArrowUpRight size={16} strokeWidth={1.5} />
            </a>
            <Link to="/cooperations" className="btn-secondary">
              Zurück zu Cooperations
            </Link>
          </div>
        </div>
      </section>

      {/* POSITIONING */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <SectionHeading
              overline="Positionierung"
              title="Qualifizierte Kontakte. Klarer Prozess. Klare Vergütung."
            />
            <p className="text-[#AEB7B7] mt-6 max-w-2xl text-base leading-relaxed">
              PINE Systems sucht Sales-affine Partner, die relevante Kontakte zu potenziellen
              Standorten herstellen. Sie müssen keine Technik installieren, keine Systeme
              betreiben und keinen Support leisten. Entscheidend ist ein qualifizierter Kontakt,
              der zu einem echten PINE Systems Projekt werden kann.
            </p>
          </div>
          <div className="lg:col-span-5 bg-[#0B0F10] border border-[#18E096]/40 p-8">
            <div className="overline text-[#18E096]">Provision</div>
            <h3 className="font-display text-2xl md:text-3xl uppercase mt-4 leading-tight">
              <span className="text-[#18E096]">899 € netto</span> bei erfolgreichem Vertragsabschluss.
            </h3>
            <p className="text-[#AEB7B7] mt-5 text-sm leading-relaxed">
              Wenn ein über Sie vermittelter, bisher nicht bekannter Lead zu einem unterzeichneten
              Vertrag mit PINE Systems führt, erhalten Sie eine Provision von 899 € netto.
            </p>
            <ul className="text-[#AEB7B7] text-xs mt-5 space-y-1.5 list-disc list-inside leading-relaxed">
              <li>Lead muss vorab über das Sales Partner Formular eingereicht werden.</li>
              <li>Der Lead darf PINE Systems noch nicht bekannt sein.</li>
              <li>Provision entsteht nur bei erfolgreicher Vertragsunterzeichnung.</li>
              <li>Auszahlung erfolgt nach Vertragsabschluss und interner Freigabe.</li>
              <li>Details werden vorab individuell bestätigt.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="border-b border-[rgba(255,255,255,0.08)] bg-[#0B0F10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading overline="Nutzen" title="Was Sie bekommen." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
            {BENEFITS.map((b, i) => (
              <div
                key={b}
                className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 bg-[#0B0F10] hover:bg-[#141819] transition-colors"
                data-testid={`sales-benefit-${i}`}
              >
                <div className="font-display text-2xl text-[#18E096]">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-base md:text-lg uppercase mt-3 text-white leading-tight">
                  {b}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading overline="Ablauf" title="Vom Profil zur Provision." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px mt-12 border-l border-t border-[rgba(255,255,255,0.08)]">
            {STEPS.map((s) => (
              <div
                key={s.n}
                className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 bg-[#0B0F10]"
                data-testid={`sales-step-${s.n}`}
              >
                <div className="font-display text-4xl text-[#18E096]">{s.n}</div>
                <h3 className="font-display text-lg uppercase mt-4 text-white leading-tight">{s.t}</h3>
                <p className="text-[#AEB7B7] text-sm mt-3 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="sales-form" className="border-b border-[rgba(255,255,255,0.08)] bg-[#0B0F10]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="overline mb-4">Anfrage</div>
          <h2 className="font-display text-3xl md:text-5xl uppercase leading-[1.05] max-w-3xl">
            Als Sales Partner <span className="text-[#18E096]">anfragen.</span>
          </h2>
          <p className="text-[#AEB7B7] mt-6 mb-8 max-w-3xl text-base leading-relaxed">
            Je präziser Sie Region, Netzwerk und Zielgruppenzugang beschreiben, desto besser
            können wir Ihre Anfrage einordnen.
          </p>
          <SalesPartnerForm />
        </div>
      </section>
    </>
  );
}
